import React, { useState } from 'react';
import { Download, Loader2, FileText, Image, Layout, Share2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { ResourceAsset } from '../../hooks/useSeriesDetail';

type AssetFilter = 'all' | 'graphic' | 'social' | 'slide' | 'outline';

const TABS: { id: AssetFilter; label: string }[] = [
  { id: 'all',     label: 'All' },
  { id: 'graphic', label: 'Graphics' },
  { id: 'social',  label: 'Social Media' },
  { id: 'slide',   label: 'Slides' },
  { id: 'outline', label: 'Outlines' },
];

const TYPE_ICON: Record<string, React.ReactNode> = {
  graphic: <Image className="h-6 w-6 text-purple-400" />,
  social:  <Share2 className="h-6 w-6 text-blue-400" />,
  slide:   <Layout className="h-6 w-6 text-amber-400" />,
  outline: <FileText className="h-6 w-6 text-emerald-400" />,
};

const TYPE_BG: Record<string, string> = {
  graphic: 'bg-purple-50 dark:bg-purple-900/20',
  social:  'bg-blue-50 dark:bg-blue-900/20',
  slide:   'bg-amber-50 dark:bg-amber-900/20',
  outline: 'bg-emerald-50 dark:bg-emerald-900/20',
};

const isImage = (asset: ResourceAsset) =>
  asset.mime_type?.startsWith('image/') ||
  /\.(png|jpg|jpeg|webp|gif|svg)$/i.test(asset.file_name);

interface Props {
  assets: ResourceAsset[];
}

export function SeriesAssetTabs({ assets }: Props) {
  const [activeTab, setActiveTab] = useState<AssetFilter>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const filtered = activeTab === 'all' ? assets : assets.filter(a => a.asset_type === activeTab);

  const handleDownload = async (asset: ResourceAsset) => {
    setDownloadingId(asset.id);
    try {
      const { data, error } = await supabase.storage
        .from('admin-assets')
        .createSignedUrl(asset.file_path, 60);
      if (error || !data) throw error;
      const link = document.createElement('a');
      link.href = data.signedUrl;
      link.download = asset.file_name;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const formatSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      {/* Tab bar */}
      <div className="flex gap-1 border-b border-sand/20 dark:border-slate/20 mb-5">
        {TABS.map(tab => {
          const count = tab.id === 'all' ? assets.length : assets.filter(a => a.asset_type === tab.id).length;
          if (tab.id !== 'all' && count === 0) return null;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-electric text-electric'
                  : 'border-transparent text-slate dark:text-soft-white/60 hover:text-midnight dark:hover:text-soft-white'
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                activeTab === tab.id
                  ? 'bg-electric/10 text-electric'
                  : 'bg-sand/30 dark:bg-slate/20 text-slate dark:text-soft-white/40'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-slate dark:text-soft-white/40 py-8 text-center">No assets in this category.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {filtered.map(asset => (
            <div
              key={asset.id}
              className="group relative flex flex-col rounded-xl border border-sand/20 dark:border-slate/20 overflow-hidden hover:border-electric/40 transition-all hover:shadow-md"
            >
              {/* Thumbnail */}
              <div className={`relative flex items-center justify-center h-32 ${TYPE_BG[asset.asset_type]}`}>
                {isImage(asset) && asset.file_path ? (
                  <img
                    src={supabase.storage.from('admin-assets').getPublicUrl(asset.file_path).data.publicUrl}
                    alt={asset.label}
                    className="w-full h-full object-cover"
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    {TYPE_ICON[asset.asset_type]}
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-slate/50 dark:text-soft-white/30">
                      {asset.asset_type}
                    </span>
                  </div>
                )}

                {/* Download overlay on hover */}
                <button
                  onClick={() => handleDownload(asset)}
                  disabled={downloadingId === asset.id}
                  className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-all opacity-0 group-hover:opacity-100"
                >
                  {downloadingId === asset.id
                    ? <Loader2 className="h-7 w-7 text-white animate-spin" />
                    : <Download className="h-7 w-7 text-white drop-shadow" />
                  }
                </button>
              </div>

              {/* Label + size */}
              <div className="flex items-center justify-between px-3 py-2 bg-white dark:bg-charcoal">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-midnight dark:text-soft-white truncate">{asset.label}</p>
                  {asset.file_size && (
                    <p className="text-[10px] text-slate/50 dark:text-soft-white/30">{formatSize(asset.file_size)}</p>
                  )}
                </div>
                <button
                  onClick={() => handleDownload(asset)}
                  disabled={downloadingId === asset.id}
                  className="shrink-0 ml-2 p-1.5 rounded-lg hover:bg-sand/20 dark:hover:bg-slate/20 text-slate dark:text-soft-white/50 disabled:opacity-40"
                >
                  {downloadingId === asset.id
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Download className="h-3.5 w-3.5" />
                  }
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
